import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import AdminService from "../../../services/admin.service";
import { HealthAlly, Sale } from "../../../types/common.types";
import Sales from "./Sales";
import formatNumberToString from "../../../utils/formatNumber";
import { useGlobal } from "../../../context/global";
import removeTimezone from "../../../utils/removeTimezone";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import { useAdmin } from "../../../context/admin";

export default function AllyInfo({id, day, endDay}: {id: string, day: Date, endDay?: Date}) {
    const { user } = useAdmin()
    const { setIsLoading } = useGlobal()
    const [data, setData] = useState<Partial<HealthAlly>>({sales: []})

    const generateSalesPDF = (sales: Sale[], totals: {
      totalSales: number;
      pending: number;
      paid: number;
      agencyEarnings: number;
      totalCommission: number;
    }) => {
      const doc = new jsPDF('landscape', 'pt', 'a4');

      // Title
      doc.setFontSize(16);
      doc.text('Sales Report', 40, 40);

      // Summary section
      const summary = [
        ['Pending', `$ ${formatNumberToString(totals.pending)}`],
        ['Paid', `$ ${formatNumberToString(totals.paid)}`],
        ['Total Commission', `$ ${formatNumberToString(totals.totalCommission)}`],
        ['Total Sales', `$ ${formatNumberToString(totals.totalSales)}`],
        ['Agency Earnings', `$ ${formatNumberToString(totals.agencyEarnings)}`],
      ];
      autoTable(doc, {
        startY: 60,
        body: summary,
        theme: 'plain',
        styles: { fontSize: 10 },
        columnStyles: {
          0: { cellWidth: 120 },
          1: { cellWidth: 100 },
        },
        margin: { left: 40 },
      });

      // Table headers – match the columns from SaleComponent
      const headers = [
        'Ally',
        'Fecha de venta',
        'Precio de venta',
        'Precio de traspaso',
        'Forma de pago',
        'Tickets',
        'Operation cost',
        'Ganancia agencia',
        '% comision',
        'Comision',
        'Auth',
      ];

      // Map each sale to a row
      const rows = sales.map((sale) => {
        const date = removeTimezone(sale.reservation.created_at.toString());
        const commission = sale.amount || (sale.reservation.price * sale.ally.sell_commission) / 100;
        const groupCosts = sale.reservation.trip?.costs?.length > 0
          ? sale.reservation.trip.costs.map(obj => obj.value).reduce((a, b) => a + b, 0) / sale.reservation.trip.reservations
          : 0;
        const costs = groupCosts + (sale.reservation.cost || 0);
        const agencyProfit = sale.reservation.trip?.agency.id !== user?.user.agency_id
          ? sale.reservation.price - sale.reservation.traspass_price - commission
          : sale.reservation.price - costs - commission;

        return [
          sale.ally?.name || 'N/A',
          date,
          formatNumberToString(sale.reservation.price),
          formatNumberToString(sale.reservation.traspass_price),
          sale.reservation.payment_methods.map(m => m.name).join(' - '),
          sale.reservation.tickets,
          formatNumberToString(costs || 0),
          formatNumberToString(agencyProfit),
          sale.amount ? `${formatNumberToString((sale.amount * 100) / sale.reservation.price)}%` : `${formatNumberToString(sale.ally?.sell_commission || 0)}%`,
          formatNumberToString(commission),
          sale.authorized ? 'Yes' : 'No',
        ];
      });

      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: (doc as any).lastAutoTable.finalY + 20,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
        columnStyles: {
          0: { cellWidth: 80 },
          1: { cellWidth: 70 },
          2: { cellWidth: 70 },
          3: { cellWidth: 90 },
          4: { cellWidth: 50 },
          5: { cellWidth: 70 },
          6: { cellWidth: 80 },
          7: { cellWidth: 60 },
          8: { cellWidth: 60 },
          9: { cellWidth: 40 },
        },
        margin: { left: 40, right: 40 },
      });

      doc.save(`sales_report_${new Date().toISOString().split('T')[0]}.pdf`);
    };


    useEffect(() => {
        async function getInfo() {
            setIsLoading(true)
            try {
                if (!day) return setData({} as HealthAlly)
                const response = await AdminService.helthByDayAlly(id, day, endDay)            
                setData({sales: response.data.sales})
            } catch (error) {
                console.log(error)
            } finally {
                setIsLoading(false)
            }
        }
        getInfo()
    }, [id, day, endDay])
    const total_sales = data.sales?.map(sale => sale.reservation.price).filter(amount => amount).reduce((a: number,b: number) => a + b, 0) || 0

    const agency_earnings = data.sales?.map(sale => {
        const groupCosts = sale.reservation.trip?.costs?.length > 0 ? sale.reservation.trip?.costs?.map(obj => obj.value).reduce((a, b) => a + b, 0) / sale.reservation.trip?.reservations : 0
        const costs = groupCosts + (sale.reservation.cost || 0)
        const commission = sale.amount || ((sale.reservation.price * (sale.ally?.sell_commission || 0)) / 100)
        return (sale.reservation.traspass_price ? 
        sale.reservation.price - sale.reservation.traspass_price - commission  
        : sale.reservation.price - costs - commission)
    })?.reduce((a,b) => a + b, 0)
    console.log(agency_earnings)

    const pending = useMemo(() => {
        let total = 0
        if (!data.sales) return 0
        for (let i = 0; i < data.sales.length; i++) {
            const sale = data.sales[i];
            if (!sale.authorized) {
                const commission = sale.amount || (sale.reservation_price * (sale.ally?.sell_commission || 0) / 100)
                total = total + commission
                continue;
            }
        }
        return total
    }, [data])
    const paid = useMemo(() => {
        let total = 0
        if (!data.sales) return 0
        for (let i = 0; i < data.sales.length; i++) {
            const sale = data.sales[i];
            if (sale.authorized) {
                total = total + sale.amount
                continue;
            }
        }
        return total
    }, [data])

    const totalCommission = useMemo(() => {
        let total = 0
        if (!data.sales) return 0
        total = paid + pending
        return total
    }, [paid, pending, data.sales])

    const handleDownload = () => {
      if (!data.sales?.length) {
        alert('No sales to download');
        return;
      }
      generateSalesPDF(data.sales, {
        totalSales: total_sales,
        pending,
        paid,
        agencyEarnings: agency_earnings || 0,
        totalCommission,
      });
    };

    return (
        <Main>
            <div className="accounting">
                <div>
                    pending 
                    <span>$ {formatNumberToString(pending)}</span>
                </div>
                <div>
                    pagado
                    <span>$ {formatNumberToString(paid)}</span>
                </div>
            </div>
            <div className="accounting">
                <div>
                    Comisión total
                    <span>$ {formatNumberToString(paid + pending)}</span>
                </div>
                <div>
                    Venta total
                    <span>$ {formatNumberToString(total_sales)}</span>
                </div>
                <div>
                    Ganancia agencia
                    <span>$ {formatNumberToString(agency_earnings || 0)}</span>
                </div>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                  <DownloadButton onClick={handleDownload}>
                    Download list
                  </DownloadButton>
                </div>
            </div>
            {
                !data.sales?.length ?
                <h1>No hay ventas</h1>
                :
                <Sales
                sales={data.sales}
                setData={setData}
                />
            }
        </Main>
    )
}

const Main = styled.div`
display: flex;
justify-content: center;
align-items: center;
gap: 15px;
flex-direction: column;
margin-bottom: 20px;
.accounting {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;
    gap: 15px;
    div span {
        margin-left: 10px;
        background-color: ${props => props.theme.blue};
        color: ${props => props.theme.white};
        padding: 5px 10px;
    }
}
`

const DownloadButton = styled.button`
  background-color: ${props => props.theme.blue};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 25px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  &:hover {
    background-color: ${props => props.theme.blueDark};
  }
`;